import {
  ApartmentOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  FolderOpenOutlined,
  KeyOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MailOutlined,
  PlayCircleOutlined,
  RocketOutlined,
  SettingOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Divider, Layout, Menu } from 'antd';
import { ItemType } from 'antd/lib/menu/interface';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import styles from './SideNav.module.css';

import { EmptyBellIconComponent, ItemWithIcon, MoonIconComponent, SunIconComponent } from '../_shared/IconControls';
import SidebarNameHeader from '../_shared/SidebarNameHeader';
import useResponsive from '../hooks/use-responsive';
import { AppThemeContext } from '../hooks/use-theme';
import { useDashboardsSSE } from '../pages/dashboards/dashboard-queries';
import { getDashboardNavMain } from '../pages/dashboards/views/DashboardNav';
import { useDocumentation } from '../pages/documentation/documentation-hooks';
import { getDocumentationNav } from '../pages/documentation/views/DocumentationNav';
import { getExtAppURL, getExtAppsNav } from '../pages/ext-apps/views/ExtAppsNav';
import { GlobalSearchInputs } from '../pages/global-search/views/GlobalSearchView';
import { useJobsSSE } from '../pages/jobs/jobs-queries';
import { getJobsNav } from '../pages/jobs/views/JobsNav';
import { getMonitoringAppsNav } from '../pages/monitoring/views/MonitoringAppsNav';
import { useServicesSSE } from '../pages/services/services-queries';
import { getServicesNav } from '../pages/services/views/ServicesNav';
import {
  ADD_GITLAB_API_KEY,
  API_GEN_ITEM_KEY,
  EMAIL_ITEM_KEY,
  LOGOUT_ITEM_KEY,
  USERNAME_ITEM_KEY,
  USER_PREFERENCES_KEY,
} from '../pages/user-info/models/user-info';
import { useUserInfo } from '../pages/user-info/user-info-hook';
import { AddGitlabApiKeyDialog } from '../pages/user-info/views/AddGitlabApiKeyDialog';
import { GenerateApiKeyDialog } from '../pages/user-info/views/GenerateApiKeyDialog';
import { UserInfo } from '../pages/user-info/views/UserInfo';
import { UserPreferencesDialog } from '../pages/user-info/views/UserPreferencesDialog';
import { SignedInUser, signOut } from '../services/auth';
import {
  DATA_QUERY_KEY,
  EXT_APPS_KEY,
  FILE_EXPLORER_KEY,
  INSTANCES_ITEM_KEY,
  MY_DATA_ACTIVITY_KEY,
  NOTIFICATIONS_ITEM_KEY,
  PUBLISHED_MANIFESTS_KEY,
  RAW_FEED_DATA_KEY,
  RESOURCES_ITEM_KEY,
  SPRINGBOARD_BROWSER_KEY,
  SUPERSET_VISUALISATIONS_KEY,
  THEME_ITEM_KEY,
  WORKFLOWS_KEY,
  searchableMenuItemLabelMap,
  subPathMatches,
} from './sidenav-constants';
import { useSideNav } from './sidenav-hooks';

type MenuItem = Required<MenuProps>['items'][number];

function getItem(label: React.ReactNode, key: React.Key, children?: MenuItem[], disabled?: boolean): MenuItem {
  return {
    key,
    label,
    children,
    disabled,
  };
}

function getItemWithIcon(label: React.ReactNode, key: React.Key, icon?: React.ReactNode, children?: MenuItem[]): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  };
}

const menuPathOverrides = [
  'workflow',
  'data',
  'file-explorer',
  'documentation/midas-landing-page',
  'documentation/midas-user-guide',
  'documentation/thw-user-guide',
];

export type SideNavProps = {
  user: SignedInUser;
  onMenuItemClick?: (menuKey: string) => void;
};

function SideNav({ user, onMenuItemClick }: SideNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [baseOpenKeys, setBaseOpenKeys] = useState<string[]>([]);

  // Force children dropdown to close when parent menu closes
  const [forceCloseChildren, setForceCloseChildren] = useState(false);

  const { theme, toggleTheme } = useContext(AppThemeContext);
  const { jobs, isLoading: jobsLoading } = useJobsSSE();
  const { services, isLoading: servicesLoading } = useServicesSSE();
  const { sideNavModel } = useSideNav();
  const { dashboards, isLoading: areDashboardsLoading } = useDashboardsSSE();

  const { userInfoModel, userInfoDelegate } = useUserInfo();
  const { documentationModel } = useDocumentation();

  const {
    isMidasPeer,
    branding: { instanceName },
  } = window.CONFIG;

  const items = useMemo(() => {
    const { featureFlags, workbenchAllowed, isLsegUser } = userInfoModel;
    const isWbOrLsegUser = isLsegUser || workbenchAllowed;

    const commonItems: ItemType[] = [];

    if (workbenchAllowed && dashboards) {
      commonItems.push(getDashboardNavMain({ dashboards, isLoading: areDashboardsLoading }));
    }

    if (workbenchAllowed) {
      commonItems.push(getServicesNav({ services, loading: servicesLoading })); // shown to the user as "Applications"

      commonItems.push(getJobsNav({ jobs, loading: jobsLoading }));

      commonItems.push(
        getItemWithIcon(
          <span data-testid="side-nav-instances">{`${instanceName} Instances`}</span>,
          INSTANCES_ITEM_KEY,
          <DesktopOutlined />
        )
      );

      commonItems.push(
        getItemWithIcon(
          <span data-testid="side-nav-springboard-browser">Springboard File Browser</span>,
          SPRINGBOARD_BROWSER_KEY,
          <PlayCircleOutlined />
        )
      );

      if (window.CONFIG.supersetDomain) {
        commonItems.push(
          getItemWithIcon(
            <span data-testid="side-nav-superset-ui">Visualisation App</span>,
            SUPERSET_VISUALISATIONS_KEY,
            <LineChartOutlined />
          )
        );
      }

      commonItems.push(
        getItemWithIcon(
          <span data-testid="side-nav-published-manifests">Published Manifests</span>,
          PUBLISHED_MANIFESTS_KEY,
          <RocketOutlined />
        ),
        getItemWithIcon(
          <span data-testid="side-nav-resources">{searchableMenuItemLabelMap.RESOURCES}</span>,
          RESOURCES_ITEM_KEY,
          <CloudServerOutlined />
        )
      );
    }

    commonItems.push(
      getItemWithIcon(
        <span data-testid="side-nav-file-exchange">{searchableMenuItemLabelMap.FX}</span>,
        FILE_EXPLORER_KEY,
        <FolderOpenOutlined />
      )
    );

    if (isMidasPeer) {
      commonItems.push(getItemWithIcon(<span data-testid="side-nav-datas-query">Data Query</span>, DATA_QUERY_KEY, <LineChartOutlined />));
    }

    if (isMidasPeer || isWbOrLsegUser) {
      commonItems.push(getItemWithIcon(<span data-testid="side-nav-workflows">Workflows</span>, WORKFLOWS_KEY, <ApartmentOutlined />));
      commonItems.push(getItemWithIcon(<span data-testid="side-nav-activity">My Data</span>, MY_DATA_ACTIVITY_KEY, <DatabaseOutlined />));

      commonItems.push(
        getItemWithIcon(<span data-testid="side-nav-raw-feed-data">Raw Feed Data</span>, RAW_FEED_DATA_KEY, <TableOutlined />)
      );
    }

    const monitoringAppsNav = getMonitoringAppsNav(isMidasPeer, isWbOrLsegUser, featureFlags);
    // the user might not have access to any monitoring apps
    if (monitoringAppsNav.children.length) {
      commonItems.push(monitoringAppsNav);
    }

    commonItems.push(getExtAppsNav());

    if (!isMobile) {
      return commonItems;
    }

    return [
      ...commonItems,
      getItem(<ItemWithIcon value={user.attributes?.name ?? ''} Icon={UserOutlined} />, USERNAME_ITEM_KEY, undefined, true),
      getItem(<ItemWithIcon value={user.attributes?.email ?? ''} Icon={MailOutlined} />, EMAIL_ITEM_KEY, undefined, true),
      getDocumentationNav({ model: { ...documentationModel, isMobile } }),
      getItem(<ItemWithIcon value="Generate API key" Icon={KeyOutlined} />, API_GEN_ITEM_KEY),
      isMidasPeer ? getItem(<ItemWithIcon value="GitLab API key" Icon={KeyOutlined} />, ADD_GITLAB_API_KEY) : null,
      getItem(<ItemWithIcon value="Logout" Icon={LogoutOutlined} />, LOGOUT_ITEM_KEY),
      getItem(<Divider />, 'divider-1'),
      getItem(<ItemWithIcon value="Toggle Notifications" Icon={EmptyBellIconComponent} />, NOTIFICATIONS_ITEM_KEY),
      workbenchAllowed ? getItem(<ItemWithIcon value="Preferences" Icon={SettingOutlined} />, USER_PREFERENCES_KEY) : null,
      getItem(
        <ItemWithIcon
          value={theme === 'light' ? 'Dark Mode' : 'Light Mode'}
          Icon={theme === 'light' ? MoonIconComponent : SunIconComponent}
        />,
        THEME_ITEM_KEY
      ),
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    services,
    servicesLoading,
    jobs,
    jobsLoading,
    // not used inside, but it seems to affect it anyway?
    forceCloseChildren,
    userInfoModel.featureFlags,
    userInfoModel.workbenchAllowed,
    userInfoModel.preferences,
    isMidasPeer,
    isMobile,
    user,
    theme,
    dashboards,
    areDashboardsLoading,
    instanceName,
    sideNavModel,
    baseOpenKeys,
  ]);

  useEffect(() => {
    const regex = new RegExp(`^/(${menuPathOverrides.join('|')})`);
    setSelectedKeys([location.pathname.match(regex)?.[0] ?? location.pathname]);
  }, [location.pathname]);

  const handleItemClick: MenuProps['onClick'] = (menuInfo) => {
    setBaseOpenKeys([]);
    // Disable navigation for disabled items
    const eventHtmlElement = menuInfo.domEvent.target as HTMLElement;
    const isMenuDisabled =
      eventHtmlElement.classList.contains('ant-dropdown-menu-item-disabled') ||
      eventHtmlElement.parentElement?.classList.contains('ant-dropdown-menu-item-disabled');

    if (menuInfo.key?.startsWith('/') && !isMenuDisabled) {
      let target = menuInfo.key;
      if (target.endsWith('/_')) {
        target = target.substring(0, target.length - 2);
      }

      onMenuItemClick?.(target);
      void navigate(target);
      return;
    }

    if (menuInfo.key?.startsWith(EXT_APPS_KEY)) {
      const appId = menuInfo.key.slice(`${EXT_APPS_KEY}/`.length);
      const appUrl = getExtAppURL(appId);
      if (appUrl) {
        window.open(appUrl, '_blank');
      }

      onMenuItemClick?.(EXT_APPS_KEY);
      return;
    }

    if (menuInfo.key === LOGOUT_ITEM_KEY) {
      signOut();
      return;
    }

    if (menuInfo.key === API_GEN_ITEM_KEY && userInfoDelegate) {
      userInfoDelegate.onStartGenerateApiKey();
    }

    if (menuInfo.key === ADD_GITLAB_API_KEY && userInfoDelegate) {
      void userInfoDelegate.onStartAddGitlabApiKey();
    }

    if (menuInfo.key === USER_PREFERENCES_KEY && userInfoDelegate) {
      void userInfoDelegate.onOpenUserPreferences();
    }

    if (menuInfo.key === THEME_ITEM_KEY) {
      toggleTheme();
      return;
    }

    onMenuItemClick?.(menuInfo.key);
  };

  if (!userInfoDelegate) {
    return null;
  }

  const handleOpenChange = (openMenuKeys: string[]) => {
    const openMenuKeysWithoutDuplicates = [...new Set(openMenuKeys)];
    const isOpen = openMenuKeys.length !== 0;
    setForceCloseChildren(!isOpen);
    setBaseOpenKeys(openMenuKeysWithoutDuplicates);
  };

  const openKeys = baseOpenKeys.slice();

  if (!sideNavModel.collapsed) {
    for (const [key, prefix] of Object.entries(subPathMatches)) {
      if (selectedKeys.find((k) => k === key || k.startsWith(prefix))) {
        openKeys.push(key);
      }
    }
  } else {
    for (const [key, prefix] of Object.entries(subPathMatches)) {
      if (selectedKeys.find((k) => k === key || k.startsWith(prefix)) && !selectedKeys.includes(key)) {
        selectedKeys.push(key);
      }
    }
  }

  const { splitName } = window.CONFIG.branding;
  const captionText = splitName.primary.toLocaleUpperCase();
  const subHeaderText = sideNavModel.collapsed ? splitName.secondaryShort : splitName.secondaryLong;

  return (
    <>
      <Layout.Sider
        collapsed={sideNavModel.collapsed}
        className={`${isMobile ? 'mobile' : 'web'}-sider ${sideNavModel.collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
        width={278}
        aria-label="Navigation Sidebar"
      >
        {!isMobile && (
          <>
            <SidebarNameHeader
              linkTarget={'/'}
              className="app-header-container"
              captionText={captionText}
              subHeaderText={subHeaderText}
              captionTextLabel={captionText}
              subHeaderTextLabel={subHeaderText}
            />
            {/* not sure if this is just workbench if we use it to search documentation */}
            {userInfoModel.workbenchAllowed && (
              <div className={styles.globalSearchDesktopContainer}>
                <GlobalSearchInputs />
              </div>
            )}
          </>
        )}
        <Menu
          className={`${isMobile ? 'mobile' : 'web'}-side-menu customize-menu-disable-btn`}
          mode="inline"
          theme={theme === 'light' ? 'light' : undefined}
          inlineIndent={16}
          onClick={handleItemClick}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={items}
        />
        {!isMobile && (
          <div className="side-menu-bottom">
            <UserInfo user={user} signOut={signOut} />
          </div>
        )}
      </Layout.Sider>
      {userInfoModel.generateApiKey && <GenerateApiKeyDialog model={userInfoModel.generateApiKey} delegate={userInfoDelegate} />}
      {userInfoModel.addGitlabApiKey && <AddGitlabApiKeyDialog model={userInfoModel.addGitlabApiKey} delegate={userInfoDelegate} />}
      {userInfoModel.userPreferencesModal && (
        <UserPreferencesDialog model={userInfoModel.userPreferencesModal} delegate={userInfoDelegate} />
      )}
    </>
  );
}

export default SideNav;
